/**
 * Action Center time formatting (2026-09-16 redesign).
 *
 * Every card shows an absolute date + time in the PRACTICE timezone (`GENERAL_TIMEZONE`) plus a
 * relative age. Instants come from the backend as ISO UTC / Firestore Timestamps and are converted
 * here with Intl — no moment, so this file stays import-free and node-tests execute it directly
 * (cypress/node-tests/action-center-grouping.test.js).
 *
 * ONE EXCEPTION: an online booking's appointment date/time is office WALL CLOCK (what the patient
 * picked on the scheduler). It is reformatted only, never run through a timezone — the online
 * scheduling double-conversion bug (10:30 AM shown as 4:30 AM) came from exactly that.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatterCache = {};

/**
 * A stored timezone name Intl accepts, or undefined (= the browser's own zone). An unknown or
 * Windows-style name must not throw inside a render.
 */
export function resolveTimeZone(tz) {
  const name = tz === null || tz === undefined ? '' : String(tz).trim();
  if (!name) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return name;
  } catch (e) {
    return undefined;
  }
}

function zonedParts(ms, timeZone, key, options) {
  const cacheKey = `${key}|${timeZone || ''}`;
  if (!formatterCache[cacheKey]) {
    formatterCache[cacheKey] = new Intl.DateTimeFormat('en-US', timeZone ? { ...options, timeZone } : options);
  }
  const out = {};
  formatterCache[cacheKey].formatToParts(new Date(ms)).forEach((p) => { out[p.type] = p.value; });
  return out;
}

function isUsableMs(ms) {
  return typeof ms === 'number' && Number.isFinite(ms) && ms > 0;
}

/** "Sep 16, 2026 · 9:42 am" in the given zone; '' for a missing instant. */
export function formatDateTime(ms, timeZone) {
  if (!isUsableMs(ms)) return '';
  const p = zonedParts(ms, timeZone, 'datetime', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
  return `${p.month} ${p.day}, ${p.year} · ${p.hour}:${p.minute} ${String(p.dayPeriod || '').toLowerCase()}`;
}

/** Calendar day of an instant in the given zone, as 'YYYY-MM-DD' (drives Today / Earlier). */
export function zonedDayKey(ms, timeZone) {
  if (!isUsableMs(ms)) return '';
  const p = zonedParts(ms, timeZone, 'day', { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${p.year}-${p.month}-${p.day}`;
}

function parseDayKey(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || '').trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const utc = Date.UTC(y, mo - 1, d);
  const check = new Date(utc);
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
  return { y, mo, d, utc, weekday: check.getUTCDay() };
}

/** Whole calendar days from one 'YYYY-MM-DD' to another (negative when `to` is earlier); null if either is bad. */
export function dayDiff(fromKey, toKey) {
  const a = parseDayKey(fromKey);
  const b = parseDayKey(toKey);
  if (!a || !b) return null;
  return Math.round((b.utc - a.utc) / 86400000);
}

/** Short age for the card corner: "just now", "12m", "1h 8m", "21h", "3d". */
export function formatRelativeAge(ms, now) {
  if (!isUsableMs(ms)) return '';
  const mins = Math.floor((now - ms) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    const rem = mins % 60;
    return rem ? `${hours}h ${rem}m` : `${hours}h`;
  }
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Wall-clock time as the office entered it: "10:30 AM" → "10:30 am", "14:05" → "2:05 pm".
 * Pure string arithmetic, never a Date. Anything unrecognised is returned verbatim.
 */
export function formatWallClockTime(value) {
  const raw = value === null || value === undefined ? '' : String(value).trim();
  if (!raw) return '';
  let m = /^(\d{1,2}):(\d{2})\s*([AaPp])\.?\s*[Mm]\.?$/.exec(raw);
  if (m) return `${Number(m[1])}:${m[2]} ${m[3].toLowerCase()}m`;
  m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(raw);
  if (m) {
    const h = Number(m[1]);
    if (h > 23) return raw;
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${m[2]} ${h < 12 ? 'am' : 'pm'}`;
  }
  return raw;
}

/**
 * Online booking appointment: "Mon Sep 22 · 10:00 am" (year appended when it isn't `currentYear`).
 * `appointmentDate` is 'YYYY-MM-DD' office wall clock; an unparseable value is shown verbatim.
 */
export function formatBookingAppointment(appointmentDate, appointmentTime, { currentYear } = {}) {
  const rawDate = appointmentDate === null || appointmentDate === undefined ? '' : String(appointmentDate).trim();
  const parsed = parseDayKey(rawDate);
  let datePart = rawDate;
  if (parsed) {
    datePart = `${WEEKDAYS[parsed.weekday]} ${MONTHS[parsed.mo - 1]} ${parsed.d}`;
    if (currentYear && Number(currentYear) !== parsed.y) datePart += `, ${parsed.y}`;
  }
  return [datePart, formatWallClockTime(appointmentTime)].filter(Boolean).join(' · ');
}
